import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://myrihai.com";
const DEFAULT_OG = `${SITE_URL}/rihai-logo.png`;

/**
 * Reusable SEO component — sets title, meta description, Open Graph, Twitter card,
 * canonical URL, and optional Article JSON-LD schema.
 */
export default function SEO({
  title,
  description,
  path = "/",
  image,
  type = "website",
  article, // { publishedAt, modifiedAt, author, category, tags }
  titleSuffix = " · Rihai",
  noindex = false,
}) {
  const url = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_OG;
  const fullTitle = title
    ? /rihai\s*$/i.test(title.trim())
      ? title
      : `${title}${titleSuffix}`
    : "Rihai — A Trusted Lawyer for Your Loved One's Bail";
  const desc =
    description ||
    "Rihai connects families across India with verified bail lawyers — ranked by real High Court outcomes. No munshis. No dallaals. Empathetic legal support when it matters most.";

  const ldWebsite = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Rihai",
    url: SITE_URL,
    description: desc,
    inLanguage: ["en-IN", "hi-IN"],
  };

  const ldOrg = {
    "@context": "https://schema.org",
    "@type": ["LegalService", "LocalBusiness"],
    "@id": `${SITE_URL}/#organization`,
    name: "Rihai",
    alternateName: "रिहाई",
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/rihai-logo.png`,
      width: 450,
      height: 385,
    },
    image: `${SITE_URL}/rihai-logo.png`,
    description:
      "Rihai connects Indian families with verified bail lawyers ranked by real High Court success rates. No middlemen, transparent fees, empathetic support.",
    telephone: "+919140650818",
    email: "hello@myrihai.com",
    priceRange: "₹₹",
    currenciesAccepted: "INR",
    paymentAccepted: "UPI, Cash, Bank transfer",
    address: {
      "@type": "PostalAddress",
      addressCountry: "IN",
      addressRegion: "India",
    },
    areaServed: [
      { "@type": "Country", name: "India" },
      { "@type": "AdministrativeArea", name: "Delhi" },
      { "@type": "AdministrativeArea", name: "Uttar Pradesh" },
      { "@type": "AdministrativeArea", name: "Bihar" },
      { "@type": "AdministrativeArea", name: "Karnataka" },
      { "@type": "AdministrativeArea", name: "West Bengal" },
      { "@type": "AdministrativeArea", name: "Assam" },
      { "@type": "AdministrativeArea", name: "Maharashtra" },
      { "@type": "AdministrativeArea", name: "Tamil Nadu" },
      { "@type": "AdministrativeArea", name: "Telangana" },
      { "@type": "AdministrativeArea", name: "Rajasthan" },
      { "@type": "AdministrativeArea", name: "Gujarat" },
      { "@type": "AdministrativeArea", name: "Madhya Pradesh" },
      { "@type": "AdministrativeArea", name: "Punjab" },
      { "@type": "AdministrativeArea", name: "Haryana" },
      { "@type": "AdministrativeArea", name: "Chandigarh" },
    ],
    serviceType: [
      "Regular bail",
      "Anticipatory bail",
      "Interim bail",
      "Criminal defence",
      "Bail lawyer connection",
    ],
    knowsAbout: [
      "Bail applications",
      "Delhi High Court",
      "Allahabad High Court",
      "Patna High Court",
      "Karnataka High Court",
      "Calcutta High Court",
      "Gauhati High Court",
      "BNSS",
      "CrPC",
      "Bharatiya Nyaya Sanhita",
    ],
    availableLanguage: [
      { "@type": "Language", name: "English", alternateName: "en-IN" },
      { "@type": "Language", name: "Hindi", alternateName: "hi-IN" },
    ],
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "00:00",
        closes: "23:59",
        description: "Emergency bail support available 24/7",
      },
    ],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: "+919140650818",
        contactType: "customer support",
        areaServed: "IN",
        availableLanguage: ["en-IN", "hi-IN"],
        contactOption: "TollFree",
      },
    ],
    sameAs: [],
  };

  const ldArticle = article
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title || "",
        image: ogImage ? [ogImage] : [],
        datePublished: article.publishedAt || undefined,
        dateModified: article.modifiedAt || article.publishedAt || undefined,
        author: {
          "@type": "Organization",
          name: article.author || "Rihai Journal",
        },
        publisher: {
          "@type": "Organization",
          name: "Rihai",
          logo: {
            "@type": "ImageObject",
            url: `${SITE_URL}/rihai-logo.png`,
          },
        },
        description: desc,
        mainEntityOfPage: url,
        articleSection: article.category || "Legal",
      }
    : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Rihai" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:locale:alternate" content="hi_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* Article-specific */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedAt || ""} />
          {article.modifiedAt && <meta property="article:modified_time" content={article.modifiedAt} />}
          {article.author && <meta property="article:author" content={article.author} />}
          {article.category && <meta property="article:section" content={article.category} />}
          {(article.tags || []).map((t) => (
            <meta property="article:tag" content={t} key={t} />
          ))}
        </>
      )}

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(ldWebsite)}</script>
      <script type="application/ld+json">{JSON.stringify(ldOrg)}</script>
      {ldArticle && (
        <script type="application/ld+json">{JSON.stringify(ldArticle)}</script>
      )}
    </Helmet>
  );
}
